import {SHARED_USER_TOKEN, USER_INFO, USER_TOKEN} from '../config/storage';
import Cookies from "js-cookie";
import {GLOBAL_ATTACHMENT_PREVIEW_URL} from '../config/api';
import {ObjTest} from 'jzfz-web-utils';
import {MajorEnum} from "../config/constant";
import { hightlightText } from './highlight';

export {
    hightlightText,
}


export interface UserInfo {
    userId: string;	// 用户id
    userName: string;	// 用户姓名
    majorCode: string;	// 用户主专业代码
    majorName: string;	// 用户主专业名称
    mobile: string;	// 手机号
    headImage?: string;	// 用户头像地址
    departmentName?: string;	// 公司名称
    companyId?: string;	// 部门id
    companyName?: string;	// 部门名称
}


/**
 * 清除缓存
 * @param removeSharedToken 是否删除共享用户token
 */
export const clearCache = (removeSharedToken: boolean = true) => {
    localStorage.clear();
    sessionStorage.clear();
    if (removeSharedToken) {
        removeSharedUserToken();
    }
}


/**
 * 缓存用户token
 * @param data
 */
export const setUserToken = (token: string) => {
    localStorage.setItem(USER_TOKEN, token);
}


/**
 * 从缓存中获取用户token
 */
export const getUserToken = (): string => {
    const token = localStorage.getItem(USER_TOKEN);
    return token ? token : null;
}


/**
 * 缓存用户信息
 * @param data
 */
export const setUserInfo = (data: Partial<UserInfo>) => {
    localStorage.setItem(USER_INFO, JSON.stringify(data));
}


/**
 * 从缓存中获取用户信息
 */
export const getUserInfo = (): UserInfo => {
    return localStorage.getItem(USER_INFO) ? JSON.parse(localStorage.getItem(USER_INFO)) : {};
}


/**
 * 缓存共享cookie
 * @param token
 */
export const setShareUserToken = (token: string) => {
    Cookies.set(SHARED_USER_TOKEN, token);
}


/**
 * 获取共享的用户token
 */
export const getSharedUserToken = (): string => {
    return Cookies.get(SHARED_USER_TOKEN);
}


/**
 * 清除共享token
 */
export const removeSharedUserToken = () => {
    Cookies.remove(SHARED_USER_TOKEN);
}


/**
 * 编码Query参数
 * @param body
 */
export const encodeGetParams = (body: any) => {
    if (!body) {
        return '';
    }
    let newBody = {...body};
    let temp = [];
    for (let k in newBody) {
        if (newBody[k] === null || newBody[k] === undefined) {
            temp.push(`${k}=`);
        } else {
            temp.push(`${k}=${encodeURIComponent(newBody[k])}`);
        }
    }
    return `${temp.join('&')}`;
}


/**
 * 获取附件预览地址
 * @param filePath 附件地址
 */
export const getPreviewUrl = (filePath: string): string => {
    if (!filePath) {
        return '';
    }
    const base64Url = btoa(filePath);
    const encodeUri = encodeURIComponent(base64Url);
    return `${GLOBAL_ATTACHMENT_PREVIEW_URL}/onlinePreview?url=${encodeUri}`;
}


/**
 * 获取Table高度（用于表头固定表格）
 * @param offsetHeight 偏移高度
 * @param selector css选择器
 */
export function getTableHeight(offsetHeight: number, selector?: string) {
    let activeHeight = 0;
    if (selector) {
        let dom: any = document.querySelector(selector);
        if (dom) {
            activeHeight = dom.offsetHeight;
        }
    }
    return document.body.scrollHeight - offsetHeight - activeHeight;
}


/**
 * 获取展开结点key
 * @param list
 * @param expandedKeys
 * @param checkedKeys
 */
export const getExpandedKeys = (list: any[], resultExpandedKeys: string[], checkedKeys: string[], keyName: string) => {
    list.forEach(v => {
        if (ObjTest.isNotEmptyArray(v.children)) {
            getExpandedKeys(v.children, resultExpandedKeys, checkedKeys, keyName);
            let flag = v.children.some(child => checkedKeys.includes(child[keyName]) || resultExpandedKeys.includes(child[keyName]));
            if (flag) {
                resultExpandedKeys.push(v[keyName]);
            }
        }
    })
}


/**
 * 获取面包屑标题
 * @param rest
 */
export const getCrumbsTitle = (list: any[]): string => {
    return (list || []).filter((v: any) => v !== null && v !== undefined && v !== '').join('/');
}


/**
 * 递归查找能滚动的父节点
 * @param el
 */
const getScrollParentNode = (el: any) => {
    if (!el) {
        return null;
    }
    let parent = el.parentNode;
    if (!parent) {
        return null;
    }
    const {overflow} = getComputedStyle(parent);
    if (overflow === 'auto' || overflow === 'scroll') {
        return parent;
    }
    return getScrollParentNode(parent);
}


/**
 * 跳转到错误提示
 * @param options selector: 错误提示元素选择器 offsetTop: 置顶偏移量
 */
export const scrollToError = (options?: { selector?: string, offsetTop?: number }) => {
    let scrollDebounce = null;
    const {selector, offsetTop} = {selector: '.ant-form-item-explain-error', offsetTop: 0, ...options};
    if (scrollDebounce) {
        clearTimeout(scrollDebounce);
    }
    scrollDebounce = setTimeout(() => {
        let el: any = document.querySelector(selector);
        let parentNode = getScrollParentNode(el);
        if (el && parentNode) {
            el.scrollIntoView();
            const {scrollTop} = parentNode;
            parentNode.scroll({top: (scrollTop + offsetTop)});
        }
        scrollDebounce = null;
    }, 500)
}


export interface IHasAuthCodeOptions {
    // 应用id
    appId: string;
    // 专业
    major: any;
    // 权限列表
    userAuthCode: any[];
    // 额外权限code列表
    extraCodes?: string[]
}


/**
 * 判断用户是否有权限
 * @param code 权限码
 * @param options
 * @return {boolean}
 */
export const hasAuthCode = (code: string, options: IHasAuthCodeOptions): boolean => {
    const {appId, major, userAuthCode, extraCodes} = options;

    // 权限列表为空
    if (!ObjTest.isNotEmptyArray(userAuthCode)) {
        return false;
    }

    // 获取当前应用和专业的权限码
    let ret = (userAuthCode || []).find((v: any) => v.appId === appId);
    if (!ret) {
        return false;
    }

    const {majorPermissions} = ret;

    // 如果有额外权限码
    if (extraCodes !== undefined) {
        if (!(extraCodes || []).includes(code)) {
            return false;
        }
    }

    // 如果全专业包含该权限码则返回true
    let allMajorPermissions = (majorPermissions || []).find((v: any) => v.major === MajorEnum.ALL);
    if (allMajorPermissions) {
        if ((allMajorPermissions.operateCodes || []).includes(code)) {
            return true;
        }
    }

    let target = (majorPermissions || []).find((v: any) => v.major === major);
    if (!target) {
        return false;
    }

    return (target.operateCodes || []).includes(code);
}


/**
 * 获取水印属性
 */
export const getWaterMarkProps = (): any => {
    const { userName, mobile, userId } = getUserInfo();
    let props = {
        content: `${userName} ${mobile || ''}`,
        subContent: '内部学习，严禁外传，违者必究',
        userId,
    }
    return props;
}


/**
 * 获取当前年份
 */
export const getNowYear = (): number => {
    return new Date().getFullYear();
}


/**
 * 数组分组,把一个数组分成多个小数组，适合于瀑布流布局
 * 如： const list = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22];
 * waterColumnsArray(list, 5)
 * 结果：[
 *    [1, 5, 9, 13, 17, 21],
 *    [2, 6, 10, 14, 18, 22],
 *    [3, 7, 11, 15, 19],
 *    [4, 8, 12, 16, 20],
 * ]
 * @param list
 * @param size
 */
export const waterColumnsArray = (list: any[], size: number): any[] => {

	if (list.length <= size) {
        // 不足大小的数组
		const arrs = [];
        for (let i = 0; i < size; i++) {
            arrs[i] = [list[i]];
        }
        return arrs;
	}

	const data = new Array(size).fill([]);

	const arrs = [];

	const count = Math.ceil(list.length / size);

	let startIndex = 0, endIndex = count;

	for (let i = 0; i < count; i++) {
		startIndex = i * size;
		endIndex = (i + 1) * size;
		if (list.slice(startIndex, endIndex).length) {
			arrs.push(list.slice(startIndex, endIndex))
		}
	}
	arrs.forEach((v, vi) => {
		(v || []).forEach((item, ii) => {
			data[ii] = [...data[ii], item];
		})
	});

	return data;
};
